/**
 * Borow rental PDP block controller
 * A rental date + size picker — it drives the theme's native product form
 * (variant id + rental line-item properties + the native add-to-cart button
 * below). On a MIXED product (rental durations plus an in-stock retail
 * variant — Duración "Compra", plain Django SKU since 21 Aug, legacy "-N")
 * it offers two purchase modes: Rent (default, the date flow below)
 * and Buy new, which swaps the native form onto the retail variant for the
 * selected size and lets the theme's own add-to-cart flow submit it — no
 * dates, no rental properties. Sizes with no sellable retail unit are
 * disabled in buy mode; when no retail variant is in stock at all the toggle
 * stays hidden and the block is pure rental. On a retail-only product (no
 * duration variants) the block hides itself entirely and leaves the theme's
 * own picker + ATC to handle it.
 *
 * Rental flow (calendar in a modal, two months):
 *   1. Pick a start date. Sundays/holidays can't be a start (no delivery);
 *      booked dates are struck through. Starts are also capped to the booking
 *      window (MAX_ADVANCE_MONTHS ahead).
 *   2. An N-day rental runs start .. start+(N-1) INCLUSIVE, and that last day
 *      IS the return day: the item comes back ON it, matching the POS
 *      formatter and the original WooCommerce emails. A last day landing on a
 *      Sunday/holiday rolls to the next working day (never a Sunday) at the
 *      same price. The short try-on duration is never offered
 *      (HIDDEN_DURATIONS).
 *   3. Pick that return day -> fixes the duration variant -> availability
 *      confirmed -> native buy button enabled. There is no separate
 *      "return by" date; the range simply ends on the day the item comes back.
 *
 * Data path: storefront -> App Proxy (/apps/borow/*) -> service layer -> Django.
 *
 * HANDOVER NOTE: two business rules are enforced here on the frontend as an
 * interim measure only: (a) roll the last day past a Sunday/holiday, (b) the
 * MAX_ADVANCE_MONTHS booking window. Django is the system of record for
 * logistics, so both should move server-side at handover. _rental_dates.end
 * carries the picked return day (already rolled), which is also the
 * Django-facing checkin.
 */

/* global flatpickr */

const PROXY = {
  CALENDAR: '/apps/borow/calendar',
  AVAILABILITY: '/apps/borow/availability'
};

// Last-resort fallback if the labels JSON fails to parse; the real, locale-
// aware strings always come from Liquid (locales/en.default.json, es.json).
const DEFAULT_LABELS = {
  chooseDates: 'Select your dates',
  pickStart: 'Choose your start date',
  pickEnd: 'Now choose your return date',
  from: 'from',
  checking: 'Checking availability…',
  available: 'Available for these dates',
  unavailable: 'Not available for these dates',
  noStartSunday: 'No delivery that day, pick another start date',
  noEnds: 'No rental length fits from that day, try another start date',
  returnExtended: 'Ends on a Sunday, so it runs to the next working day at no extra cost. Still the {days}-day rate.',
  clear: 'Change dates',
  soldOut: 'Sold out',
  lockedToCart: 'Matching the dates already in your basket.',
  lockedNoDuration: "This item doesn't offer the same rental length as the one in your basket. Empty your basket to book it separately.",
  lockedUnavailable: "Not available for this item on your basket's dates. Empty your basket to book it separately.",
  rangeChooseEnd: '{start} — choose end',
  rangeSummary: '{start} – {end}',
  pickEndDurations: 'Choose your end date: {durations} days',
  orWord: 'or',
  availableRate: 'Available · {days}-day rate',
  extNoteOne: 'Incl. 1 extra day at no added cost',
  extNoteMany: 'Incl. {days} extra days at no added cost',
  priceBuy: 'Buy new',
  priceRentFrom: 'Rent from',
  priceRentDays: '{days}-day rental',
  // Storefront language (request.locale.iso_code) — dates must follow the
  // store's language, not the visitor's device locale.
  locale: null,
  // Booking window in months, from Theme settings -> Borow rental.
  maxAdvanceMonths: null,
  currency: 'EUR'
};

const DAY_MS = 86400000;

// Substitute {name} placeholders in a translated label.
const fill = (template, values) =>
  String(template || '').replace(/\{(\w+)\}/g, (m, k) => (values[k] != null ? String(values[k]) : m));

// Durations that exist as variants but must NOT be offered as a customer rental
// period on the storefront (e.g. the short try-on window, which staff schedule
// internally and is never booked directly by a customer).
const HIDDEN_DURATIONS = new Set([2]);

// How far ahead a rental may be started. Merchant-editable via Theme settings
// -> Borow rental -> Booking window (passed in through the labels JSON); this
// constant is only the fallback when the setting is missing. Interim frontend
// cap pending backend ownership (see header note).
const MAX_ADVANCE_MONTHS = 2;

function ymd(d) {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
function startOfDay(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
// Parse YYYY-MM-DD as a LOCAL midnight. new Date('2026-08-06') would be parsed
// as UTC and land on the previous day west of Greenwich.
function parseYmd(s) { const [y, m, d] = String(s).split('-').map(Number); return new Date(y, m - 1, d); }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function addMonths(d, n) { const r = new Date(d); r.setMonth(r.getMonth() + n); return r; }
function daysBetween(a, b) { return Math.round((startOfDay(b) - startOfDay(a)) / DAY_MS); }

function findDurationIndex(optionNames) {
  return (optionNames || []).findIndex((n) => /^(duration|duraci[oó]n)$/i.test(String(n).trim()));
}
function isTypeToken(str) {
  const s = String(str || '').toLowerCase();
  return /resale|pre.?loved|second|segunda|retail|\bnew\b|nuevo/.test(s) || /\d+\s*(?:d|day|días|dias)/.test(s);
}
function classifyVariant(v, optionNames) {
  const durIdx = findDurationIndex(optionNames);
  const token = durIdx >= 0 ? String((v.options || [])[durIdx] || '') : '';
  const haystack = (token || `${v.title || ''} ${(v.options || []).join(' ')}`).toLowerCase();
  const sku = String(v.sku || '').toUpperCase();
  if (/resale|pre.?loved|second|\bsegunda\b/.test(haystack) || /-S\d*$/.test(sku)) return { type: 'resale', days: null };
  if (/retail|\bnew\b|\bnuevo\b/.test(haystack) || /-N\d*$/.test(sku)) return { type: 'retail', days: null };
  const skuDays = sku.match(/-R(\d+)$/);
  const textDays = haystack.match(/(\d+)\s*(?:d|day|días|dias)/);
  const days = skuDays ? Number(skuDays[1]) : textDays ? Number(textDays[1]) : null;
  // Retail variants carry the plain Django SKU since 21 Aug (the -N suffix was
  // dropped for the 3PL), so absence is the signal: a Duración value that is
  // neither a day count nor resale is the buy-new variant. Mirrors the service
  // layer's classifyVariant; the -N test above still covers pre-change data.
  if (!days && token) return { type: 'retail', days: null };
  return { type: 'rental', days };
}
function deriveSize(v, optionNames) {
  const opts = v.options || [];
  const durIdx = findDurationIndex(optionNames);
  if (durIdx >= 0) return opts.filter((_, i) => i !== durIdx).join(' / ');
  return opts.filter((o) => o && !isTypeToken(o)).join(' / ');
}
function baseSku(sku) { return String(sku || '').replace(/-(R\d+|S\d*|N\d*)$/i, ''); }

/**
 * Size ordering
 * Variant options stream in storage order, which the catalogue audit (24 Aug,
 * 987 products) showed is arbitrary. Ranks cover every label family found:
 * the dress ladder (XXS/32 .. 5XL/50), letter ranges (XS/S .. L/XL), bare
 * letters, numeric shoe sizes (35/38, 39/41) and Unica last. Unknown labels
 * (colour values on Tono products, cup sizes) fall to a stable alphabetical
 * tail rather than throwing.
 */
const SIZE_LADDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];
function sizeRank(label) {
  const s = String(label || '').toUpperCase().trim();
  if (/^[UÚ]NICA$|^ONE ?SIZE$/.test(s)) return { tier: 3, main: 0, sub: 0 };
  let alphaSum = 0; let alphaCount = 0; let num = null;
  for (const t of s.split(/\s*[/-]\s*/)) {
    const xl = t.match(/^([3-9])XL$/);
    const idx = xl ? SIZE_LADDER.length + Number(xl[1]) - 3 : SIZE_LADDER.indexOf(t);
    if (idx >= 0) { alphaSum += idx; alphaCount += 1; }
    else if (num === null && /^\d+$/.test(t)) num = Number(t);
  }
  if (alphaCount) return { tier: 0, main: alphaSum / alphaCount, sub: num || 0 };
  if (num !== null) return { tier: 1, main: num, sub: 0 };
  return { tier: 2, main: 0, sub: 0 };
}
function compareSizes(a, b) {
  const ra = sizeRank(a.label); const rb = sizeRank(b.label);
  return (ra.tier - rb.tier) || (ra.main - rb.main) || (ra.sub - rb.sub)
    || String(a.label).localeCompare(String(b.label), 'es');
}

class BorowRental extends HTMLElement {
  constructor() {
    super();
    this.state = {
      mode: 'rent',
      sizeKey: null,
      phase: 'start',
      startDate: null,
      endDate: null,
      extended: false,
      snappedDays: null,
      candidateEnds: new Set(),
      endMap: {},
      availability: 0,
      calendarCache: new Map(),
      // Dates already committed by a rental in the basket, and why they can't
      // be honoured on this product (null = honoured, or nothing in the basket).
      lock: null,
      lockError: null
    };
    this.picker = null;
  }

  connectedCallback() {
    this.labels = { ...DEFAULT_LABELS, ...this.readJson('[data-borow-labels]') };
    const data = this.readJson('[data-borow-variants]') || {};
    this.optionNames = data.optionNames || [];
    this.model = this.buildModel(data.variants || []);

    // Retail-only product (no rental durations) -> hide; native picker/ATC handles it.
    if (!this.model.hasRental) { this.hidden = true; return; }

    // Rentals undeliverable in this market (rental shipping covers ES/PT
    // only): show buy-only, and hide entirely when there is nothing to buy -
    // the theme's native sold-out state then carries the page.
    if (!this.model.hasMarketRental) {
      if (!this.model.hasBuyableRetail) { this.hidden = true; return; }
      this.state.mode = 'buy';
    }

    // Compra is the default on mixed products (client mockup, 24 Aug);
    // rent-only products stay in rent mode with the toggle hidden.
    if (this.model.hasBuyableRetail) this.state.mode = 'buy';

    this.els = {
      modes: this.querySelector('[data-borow-modes]'),
      sizeWrap: this.querySelector('[data-borow-sizes]'),
      sizesLabel: this.querySelector('[data-borow-sizes-label]'),
      priceRow: this.querySelector('[data-borow-pricerow]'),
      priceLabel: this.querySelector('[data-borow-price-label]'),
      price: this.querySelector('[data-borow-price]'),
      modalPrice: this.querySelector('[data-borow-modal-price]'),
      trigger: this.querySelector('[data-borow-open]'),
      modal: this.querySelector('[data-borow-modal]'),
      done: this.querySelector('[data-borow-done]'),
      calendarWrap: this.querySelector('[data-borow-calendar]'),
      clear: this.querySelector('[data-borow-clear]'),
      status: this.querySelector('[data-borow-status]'),
      lock: this.querySelector('[data-borow-lock]')
    };

    this.form = document.querySelector('product-form[data-type="add-to-cart-form"] form')
      || document.querySelector('product-form form');
    this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
    this.submitBtn = this.form ? this.form.querySelector('[name="add"], [type="submit"]') : null;
    if (!this.form) console.warn('[Borow] product form not found — cannot drive native add-to-cart');
    this.lockQuantity();

    // The block owns the price display (mode-driven label + value), so the
    // theme's own Price block would double-render it. Hidden while this block
    // is active; restored on disconnect so retail-only products and editor
    // block removals keep the native price.
    const section = this.closest('.shopify-section') || document;
    this.themePriceEl = section.querySelector('[id^="price-"]');
    if (this.themePriceEl) this.themePriceEl.hidden = true;
    if (this.els.priceRow) this.els.priceRow.hidden = false;

    this.renderSizes();
    this.initModes();
    const first = this.enabledSizeKeys()[0] || Object.keys(this.model.sizes)[0];
    if (first) this.selectSize(first, { silent: true });
    this.updatePrice();
    this.refreshCalendar(); // prefetch; picker builds on modal open
    this.initCartLock();    // a rental already in the basket fixes these dates

    if (this.els.trigger) this.els.trigger.addEventListener('click', () => this.openModal());
    if (this.els.done) this.els.done.addEventListener('click', () => this.closeModal());
    if (this.els.clear) this.els.clear.addEventListener('click', () => this.clearDates());
    if (this.els.modal) this.els.modal.addEventListener('click', (e) => { if (e.target === this.els.modal) this.closeModal(); });
    if (this.submitBtn) this.submitBtn.addEventListener('click', (e) => this.onAddClick(e));
  }

  disconnectedCallback() {
    if (this.picker && typeof this.picker.destroy === 'function') this.picker.destroy();
    if (this.themePriceEl) { this.themePriceEl.hidden = false; this.themePriceEl = null; }
  }

  readJson(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (err) { console.error('[Borow] parse fail', selector, err); return null; }
  }

  buildModel(variants) {
    const sizes = {}; let hasRental = false; let hasBuyableRetail = false; let hasMarketRental = false;
    const sizeEntry = (v) => {
      const label = deriveSize(v, this.optionNames);
      const sizeKey = label || '__single__';
      sizes[sizeKey] = sizes[sizeKey] || { key: sizeKey, label, sku: baseSku(v.sku), rental: {}, retail: null };
      if (v.sku) sizes[sizeKey].sku = baseSku(v.sku);
      return sizes[sizeKey];
    };
    variants.forEach((v) => {
      const { type, days } = classifyVariant(v, this.optionNames);
      if (type === 'rental' && days) {
        sizeEntry(v).rental[days] = v;
        hasRental = true;
        // Rental variants sit in the "Borow rental shipping" delivery profile
        // (ES + PT zones only), so Markets renders them unavailable everywhere
        // else. Availability here therefore means "rentable in this market".
        if (v.available) hasMarketRental = true;
      } else if (type === 'retail') {
        // Buy-new path on a mixed product; a size can also exist retail-only.
        sizeEntry(v).retail = v;
        if (v.available) hasBuyableRetail = true;
      }
      // resale variants stay ignored — resale is POS-only for now
    });
    // Chips, auto-selection and price fallbacks all iterate this object, so
    // fix the display order once here: small to large, Unica last.
    const ordered = {};
    Object.values(sizes).sort(compareSizes).forEach((s) => { ordered[s.key] = s; });
    return { sizes: ordered, hasRental, hasBuyableRetail, hasMarketRental };
  }

  /* Purchase mode (Rent / Buy new) — mixed products only. The toggle appears
   * when at least one retail variant is in stock; retail variants at quantity
   * zero (or none at all) leave the pure rental UI untouched. */

  initModes() {
    this.dataset.mode = this.state.mode;
    if (!this.els.modes) { this.applyModeToSizes(); return; }
    if (!this.model.hasBuyableRetail || !this.model.hasMarketRental) {
      this.els.modes.hidden = true;
      this.applyModeToSizes();
      return;
    }
    this.els.modes.hidden = false;
    this.setActive(this.els.modes, 'data-mode', this.state.mode);
    this.els.modes.querySelectorAll('[data-mode]').forEach((b) => b.addEventListener('click', () => this.setMode(b.dataset.mode)));
    this.applyModeToSizes();
  }

  setMode(mode) {
    if ((mode !== 'rent' && mode !== 'buy') || this.state.mode === mode) return;
    this.state.mode = mode;
    this.dataset.mode = mode;
    this.setActive(this.els.modes, 'data-mode', mode);
    this.applyModeToSizes();
    // Rental date state is kept across a round-trip to Buy new; only reselect
    // when the current size has nothing to offer in the new mode.
    const current = this.currentSize();
    if (!current || !this.sizeUsable(current)) {
      const next = this.enabledSizeKeys()[0];
      if (next) this.selectSize(next, { silent: true });
    }
    this.updatePrice();
    this.updateTrigger();
    this.syncForm();
    this.showLock();
  }

  sizeUsable(s) {
    if (this.state.mode === 'buy') return Boolean(s.retail && s.retail.available);
    return Object.keys(s.rental).length > 0;
  }

  enabledSizeKeys() { return Object.values(this.model.sizes).filter((s) => this.sizeUsable(s)).map((s) => s.key); }

  applyModeToSizes() {
    if (!this.els.sizeWrap) return;
    this.els.sizeWrap.querySelectorAll('[data-size]').forEach((b) => {
      const s = this.model.sizes[b.dataset.size];
      const usable = s ? this.sizeUsable(s) : false;
      b.disabled = !usable;
      b.classList.toggle('is-soldout', !usable);
      if (!usable && this.state.mode === 'buy') b.setAttribute('title', this.labels.soldOut);
      else b.removeAttribute('title');
    });
  }

  renderSizes() {
    if (!this.els.sizeWrap) return;
    // Rendered even for a single size, so the customer can always read what
    // size the product is. Only a product with no size option at all hides.
    // Order comes from buildModel; unavailable sizes grey via applyModeToSizes.
    const sizes = Object.values(this.model.sizes).filter((s) => s.label);
    const show = sizes.length > 0;
    this.els.sizeWrap.hidden = !show;
    if (this.els.sizesLabel) this.els.sizesLabel.hidden = !show;
    if (!show) return;
    this.els.sizeWrap.innerHTML = sizes.map((s) => `<button type="button" class="borow-rental__size" data-size="${s.key}">${s.label}</button>`).join('');
    this.els.sizeWrap.querySelectorAll('[data-size]').forEach((b) => b.addEventListener('click', () => this.selectSize(b.dataset.size)));
  }

  selectSize(sizeKey, { silent = false } = {}) {
    this.state.sizeKey = sizeKey;
    this.setActive(this.els.sizeWrap, 'data-size', sizeKey);
    this.clearDates({ silent: true });
    if (!silent && this.state.mode === 'rent') this.loadCalendar();
    this.updatePrice();
    this.syncForm();
    // Durations and blocked dates are per-variant, so a locked basket range has
    // to be re-tested against the size just chosen.
    if (this.state.lock && this.state.mode === 'rent') this.applyCartLock();
  }

  setActive(container, attr, value) {
    if (!container) return;
    container.querySelectorAll(`[${attr}]`).forEach((b) => {
      const on = b.getAttribute(attr) === value;
      b.classList.toggle('is-active', on);
      if (b.hasAttribute('aria-pressed')) b.setAttribute('aria-pressed', String(on));
    });
  }

  currentSize() { return this.model.sizes[this.state.sizeKey] || null; }
  currentSku() { const s = this.currentSize(); return s ? s.sku : null; }
  availableDurations() { const s = this.currentSize(); return s ? Object.keys(s.rental).map(Number).filter(d => !HIDDEN_DURATIONS.has(d)).sort((a, b) => a - b) : []; }

  /* Modal — picker builds on open (a display:none dialog renders nothing) */

  async openModal() {
    if (this.els.modal && typeof this.els.modal.showModal === 'function' && !this.els.modal.open) this.els.modal.showModal();
    await this.loadCalendar();
  }
  closeModal() { if (this.els.modal && this.els.modal.open) this.els.modal.close(); }

  setCalendarLoading(loading) {
    if (this.els.modal) this.els.modal.classList.toggle('is-loading', loading);
  }

  /* Render the grid immediately (so the modal is at full height with no jump),
   * spin while the occupancy data is fetched, then repaint. The spinner only
   * shows on a real fetch, not on a cached reopen. */
  async loadCalendar() {
    if (!(this.els.modal && this.els.modal.open)) return;
    this.initPicker();
    const sku = this.currentSku();
    const needsFetch = Boolean(sku && !this.state.calendarCache.has(sku));
    if (needsFetch) this.setCalendarLoading(true);
    await this.refreshCalendar();
    if (this.picker && this.picker.redraw) this.picker.redraw();
    if (needsFetch) this.setCalendarLoading(false);
  }

  /* Calendar */

  async refreshCalendar() {
    const sku = this.currentSku();
    if (!sku) return;
    let map = this.state.calendarCache.get(sku);
    if (!map) { map = await this.fetchCalendar(sku); this.state.calendarCache.set(sku, map); }
    this.calendarMap = map;
  }

  async fetchCalendar(sku) {
    const map = {};
    try {
      const res = await fetch(`${PROXY.CALENDAR}?sku=${encodeURIComponent(sku)}&type=variation`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return map;
      const body = await res.json();
      const list = Array.isArray(body) ? body : body.data || [];
      list.forEach((e) => { if (e && e.date) map[e.date] = e.text || 'blocked'; });
    } catch (err) { console.error('[Borow] calendar fetch failed', err); }
    return map;
  }

  // Latest start date the booking window allows. Recomputed (not cached) so a
  // long-lived tab never books against a stale "today".
  maxStartDate() {
    const months = Number(this.labels.maxAdvanceMonths);
    return addMonths(startOfDay(new Date()), months > 0 ? months : MAX_ADVANCE_MONTHS);
  }

  // No collection on Sundays or holidays. Sunday is derived directly (getDay 0)
  // so it holds even past the fetched calendar window; holidays come from the
  // feed. Saturdays are deliverable, matching the old site's "domingo o festivo".
  isNonWorkingDay(date) {
    if (startOfDay(date).getDay() === 0) return true;
    return (this.calendarMap || {})[ymd(date)] === 'holiday';
  }

  // Roll a return date forward to the next working day. Guarded against an
  // unbroken run (should never exceed a few days) so it can never spin.
  nextWorkingDay(date) {
    let d = startOfDay(date);
    for (let i = 0; i < 14 && this.isNonWorkingDay(d); i += 1) d = addDays(d, 1);
    return d;
  }

  canStart(date) {
    const day = startOfDay(date);
    if (day < startOfDay(new Date()) || day > this.maxStartDate()) return false;
    const type = (this.calendarMap || {})[ymd(date)];
    if (type === 'blocked' || type === 'sunday' || type === 'holiday') return false;
    // A start every rental length collides from is greyed out rather than
    // offered and then refused - the noEnds message stays only as a net for
    // data arriving after first paint.
    return this.endsFrom(day).ends.size > 0;
  }

  /* Selectable end dates from a start. An N-day rental runs start ..
   * start+(N-1) inclusive, and that last day IS the return day: the item
   * comes back ON it. When it lands on a Sunday/holiday the end rolls to the
   * next working day (never a Sunday) at the same price. endMap records the
   * paid duration and whether the day rolled (extended). Durations whose
   * window crosses a booked date are dropped; when two durations roll onto
   * the same day the first keeps it. */
  endsFrom(date) {
    const ends = new Set();
    const map = {};
    for (const d of this.availableDurations()) {
      const naturalEnd = addDays(date, d - 1);
      const end = this.nextWorkingDay(naturalEnd);
      if (this.rangeHasBlocked(date, end)) continue;
      const key = ymd(end);
      if (!ends.has(key)) { ends.add(key); map[key] = { days: d, extended: ymd(end) !== ymd(naturalEnd), extra: daysBetween(naturalEnd, end) }; }
    }
    return { ends, map };
  }

  /* End-pick instruction listing the offered durations, per the mockup:
   * "Elige tu fecha de fin: 4, 6, 8, 10 o 20 dias". */
  pickEndLabel() {
    const durations = this.availableDurations();
    if (!durations.length || !this.labels.pickEndDurations) return this.labels.pickEnd;
    const list = durations.length > 1
      ? `${durations.slice(0, -1).join(', ')} ${this.labels.orWord} ${durations[durations.length - 1]}`
      : String(durations[0]);
    return fill(this.labels.pickEndDurations, { durations: list });
  }

  extNote() {
    const extra = this.state.extendedDays || 0;
    if (!extra) return '';
    return extra === 1 ? this.labels.extNoteOne : fill(this.labels.extNoteMany, { days: extra });
  }

  initPicker() {
    if (!this.els.calendarWrap || typeof flatpickr === 'undefined') return;
    if (this.picker && typeof this.picker.destroy === 'function') this.picker.destroy();
    // Cap navigation at the window edge plus the longest rental, so a start near
    // the edge can still reach its return date, but empty far-future months
    // (with no calendar data, which would otherwise read as free) are unreachable.
    const longestDuration = Math.max(0, ...this.availableDurations());
    this.picker = flatpickr(this.els.calendarWrap, {
      inline: true,
      showMonths: window.matchMedia('(min-width: 700px)').matches ? 2 : 1,
      dateFormat: 'Y-m-d',
      minDate: startOfDay(new Date()),
      maxDate: addDays(this.maxStartDate(), longestDuration),
      locale: this.pickerLocale(),
      disable: [(date) => {
        // Any basket lock freezes the picker. When it's honoured the range is
        // shown read-only; when it isn't, leaving the calendar live would let
        // the customer pick their own dates and recreate the mixed-dates order
        // this lock exists to prevent. Emptying the basket is the only way out.
        if (this.state.lock) return true;
        if (this.state.phase === 'end') { const k = ymd(date); return !(this.state.candidateEnds.has(k) || this.canStart(date)); }
        return !this.canStart(date);
      }],
      onDayCreate: (_d, _s, _fp, dayElem) => this.paintDay(dayElem),
      onChange: (dates) => {
        const d = dates && dates[dates.length - 1];
        if (!d) return;
        if (this.state.phase === 'start') this.onStartPicked(d);
        else if (this.state.candidateEnds.has(ymd(d))) this.onEndPicked(d);
        else this.onStartPicked(d); // a valid fresh start mid-pick restarts the range from it (mockup)
      }
    });
    // A locked picker has nothing to prompt for; checkAvailability owns the line.
    if (!this.state.lock) this.setPrompt(this.state.phase === 'end' ? this.pickEndLabel() : this.labels.pickStart);
  }

  paintDay(dayElem) {
    const date = dayElem.dateObj;
    if (!date) return;
    const key = ymd(date);
    const type = (this.calendarMap || {})[key];
    if (type) dayElem.classList.add(`borow-${type}`);
    if (this.state.phase === 'end') {
      if (this.state.candidateEnds.has(key)) {
        dayElem.classList.add('borow-candidate');
      } else if (!dayElem.classList.contains('flatpickr-disabled')
        && !(this.state.startDate && key === ymd(this.state.startDate))) {
        // Valid fresh starts stay clickable (a click restarts the range from
        // them) but read as grey while picking the end, so only the candidate
        // return days look selectable - mirrors the mockup's cell states.
        dayElem.classList.add('borow-restart');
      }
    }
    // Paint the band and end marker to the picked return day (endDate) — the
    // inclusive range the customer holds the item for.
    const visualEnd = this.state.endDate;
    if (this.state.startDate && key === ymd(this.state.startDate)) dayElem.classList.add('borow-start');
    if (visualEnd && key === ymd(visualEnd)) dayElem.classList.add('borow-end');
    if (this.state.startDate && visualEnd && date > this.state.startDate && date < visualEnd) dayElem.classList.add('borow-inrange');
  }

  /**
   * True if any day in [start, end] is fully booked. Used both to exclude
   * wrap-around candidate end dates up front and as a belt-and-braces guard
   * before trusting the backend's own availability answer.
   */
  rangeHasBlocked(start, end) {
    for (let d = startOfDay(start); d <= end; d = addDays(d, 1)) {
      if ((this.calendarMap || {})[ymd(d)] === 'blocked') return true;
    }
    return false;
  }

  onStartPicked(date) {
    if (!this.canStart(date)) { this.setStatus(this.labels.noStartSunday, 'error'); return; }
    this.state.startDate = date;
    this.state.endDate = null;
    this.state.extended = false;
    this.state.extendedDays = 0;
    this.state.snappedDays = null;
    this.state.availability = 0;
    const { ends, map } = this.endsFrom(date);
    // Trap starts are greyed out by canStart, so this only fires when the
    // availability data changed between paint and click: say so and stay in
    // the start phase rather than stranding the customer (client, 23 Aug).
    if (!ends.size) {
      this.state.startDate = null;
      if (this.picker && this.picker.redraw) this.picker.redraw();
      this.setStatus(this.labels.noEnds, 'error');
      return;
    }
    this.state.candidateEnds = ends;
    this.state.endMap = map;
    this.state.phase = 'end';
    if (this.picker && this.picker.redraw) this.picker.redraw();
    // A start late in the visible range puts every candidate end in a month
    // that is not rendered, which reads as a dead calendar (the stuck screen
    // from the client's 23 Aug feedback). Jump to the earliest candidate's
    // month, but only when none is on screen - normal picks keep their view.
    this.jumpToCandidates(ends);
    // setStatus would wipe the prompt setPrompt just wrote (same element), so
    // the prompt comes last and nothing clears after it.
    this.setPrompt(this.pickEndLabel());
    if (this.els.clear) this.els.clear.hidden = false;
    this.updateTrigger();
    this.syncForm();
  }

  jumpToCandidates(ends) {
    if (!(this.picker && typeof this.picker.jumpToDate === 'function') || !ends.size) return;
    const shown = this.picker.config && this.picker.config.showMonths ? this.picker.config.showMonths : 1;
    const first = new Date(this.picker.currentYear, this.picker.currentMonth, 1);
    const limit = new Date(this.picker.currentYear, this.picker.currentMonth + shown, 1);
    const visible = [...ends].some((key) => { const d = parseYmd(key); return d >= first && d < limit; });
    if (!visible) this.picker.jumpToDate(parseYmd([...ends].sort()[0]));
  }

  onEndPicked(date) {
    const key = ymd(date);
    if (!this.state.candidateEnds.has(key)) return;
    // The customer picks the return day itself (already rolled off any
    // Sunday/holiday). It drives the variant, price, availability check and
    // _rental_dates.end — the Django-facing checkin. extended flags a day
    // that rolled past a Sunday (the rental runs on at the same rate).
    const mapping = (this.state.endMap && this.state.endMap[key])
      || { days: daysBetween(this.state.startDate, date) + 1, extended: false, extra: 0 };
    this.state.endDate = date;
    this.state.extended = mapping.extended;
    this.state.extendedDays = mapping.extra || 0;
    this.state.snappedDays = mapping.days;
    if (this.picker && this.picker.redraw) this.picker.redraw();
    this.updateTrigger();
    this.updatePrice();
    this.checkAvailability();
  }

  clearDates({ silent = false, keepStatus = false } = {}) {
    this.state.phase = 'start';
    this.state.startDate = null;
    this.state.endDate = null;
    this.state.extended = false;
    this.state.extendedDays = 0;
    this.state.snappedDays = null;
    this.state.availability = 0;
    this.state.candidateEnds = new Set();
    this.state.endMap = {};
    if (this.picker) { if (this.picker.clear) this.picker.clear(); if (this.picker.redraw) this.picker.redraw(); }
    if (this.els.clear) this.els.clear.hidden = true;
    this.updateTrigger();
    this.updatePrice();
    if (!keepStatus) this.setStatus('', '');
    this.setPrompt(this.labels.pickStart);
    if (!silent) this.syncForm();
  }

  /* Cart date lock
   *
   * Every rental in one order has to run to the same dates: the backend books
   * an order's bookings against a single window, and the plan doesn't allow a
   * cart-validation function, so the constraint is enforced here at the point
   * the customer picks. The first rental in the basket wins and every later
   * rental inherits its range read-only.
   *
   * The basket is the source of truth (not a local flag), so the lock survives
   * a reload, a new tab, and a return visit. Durations and blocked dates are
   * per-variant, so a locked range is re-checked on every size change and can
   * legitimately fail on one size and pass on another.
   */

  async readCartLock() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!res.ok) return null;
      const cart = await res.json();
      for (const item of cart.items || []) {
        const raw = (item.properties || {})._rental_dates;
        if (!raw) continue;
        let start = null;
        try { start = JSON.parse(raw).start; } catch { continue; }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(String(start))) continue;
        // Paid duration comes off the variant SKU (-R4). _rental_dates carries
        // the Django-facing checkin, which is the rolled return day, not the
        // duration, so it can't be derived from the property alone.
        const m = String(item.sku || '').toUpperCase().match(/-R(\d+)$/);
        if (!m) continue;
        return { start, days: Number(m[1]) };
      }
    } catch (err) {
      console.error('[Borow] cart lock read failed', err);
    }
    return null;
  }

  async initCartLock() {
    const lock = await this.readCartLock();
    if (!lock) return;
    this.state.lock = lock;
    await this.applyCartLock();
  }

  /* Pin this product to the basket's range, or explain why it can't be. */
  async applyCartLock() {
    const lock = this.state.lock;
    if (!lock) return;
    await this.refreshCalendar(); // nextWorkingDay/rangeHasBlocked read the map
    this.state.lockError = null;

    if (!this.availableDurations().includes(lock.days)) {
      this.state.lockError = 'duration';
      this.finishLock();
      return;
    }

    const start = parseYmd(lock.start);
    const naturalEnd = addDays(start, lock.days - 1);
    const end = this.nextWorkingDay(naturalEnd);

    // A basket that has gone stale overnight, or a range this variant can't take.
    if (start < startOfDay(new Date()) || this.rangeHasBlocked(start, end)) {
      this.state.lockError = 'unavailable';
      this.finishLock();
      return;
    }

    this.state.phase = 'locked';
    this.state.startDate = start;
    this.state.endDate = end;
    this.state.extended = ymd(end) !== ymd(naturalEnd);
    this.state.extendedDays = daysBetween(naturalEnd, end);
    this.state.snappedDays = lock.days;
    if (this.els.clear) this.els.clear.hidden = true; // a locked range isn't changeable here
    this.showLock();
    this.updateTrigger();
    this.updatePrice();
    if (this.picker && this.picker.redraw) this.picker.redraw();
    await this.checkAvailability();
  }

  finishLock() {
    this.showLock();
    this.updateTrigger();
    if (this.picker && this.picker.redraw) this.picker.redraw();
    this.syncForm();
  }

  showLock() {
    const el = this.els.lock;
    if (!el) return;
    const key = this.state.lockError === 'duration' ? 'lockedNoDuration'
      : this.state.lockError === 'unavailable' ? 'lockedUnavailable'
        : 'lockedToCart';
    el.textContent = this.labels[key] || '';
    el.dataset.tone = this.state.lockError ? 'error' : '';
    // Buy new is a retail purchase with no dates, so the lock doesn't apply.
    const applies = Boolean(this.state.lock) && this.state.mode === 'rent';
    el.hidden = !applies;
    this.dataset.locked = String(applies && !this.state.lockError);
  }

  /* Availability */

  async checkAvailability() {
    if (this.state.mode !== 'rent') return;
    const sku = this.currentSku();
    if (!sku || !this.state.startDate || !this.state.endDate) return;

    // Belt-and-braces guard for the backend web-check containment gap: a range
    // that spans any fully-booked date is unavailable, even if web-check misses
    // the wrap. Candidate-end filtering above should make this unreachable via
    // the calendar UI, but this stays as a second line of defence.
    if (this.rangeHasBlocked(this.state.startDate, this.state.endDate)) {
      this.state.availability = 0;
      if (this.els.modalPrice) this.els.modalPrice.textContent = '';
      this.setStatus(this.labels.unavailable, 'error');
      this.syncForm();
      return;
    }

    this.setStatus(this.labels.checking, 'checking');
    this.state.availability = 0;
    this.syncForm();
    try {
      const start = ymd(this.state.startDate);
      const end = ymd(this.state.endDate);
      const res = await fetch(`${PROXY.AVAILABILITY}?sku=${encodeURIComponent(sku)}&start=${start}&end=${end}&type=web`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const d = body.data || body;
      const explicit = typeof d.available === 'number' ? d.available : null;
      const forRent = d.for_rent === true || (explicit !== null && explicit > 0);
      this.state.availability = forRent ? (explicit != null ? explicit : 1) : 0;
      if (forRent) {
        this.updatePrice();
        // The range ends on the return day itself, so no separate "return by"
        // line. When the end rolled past a Sunday/holiday, reassure the
        // customer they still pay the nominal-duration rate.
        let base = fill(this.labels.availableRate, { days: this.state.snappedDays });
        const note = this.extNote();
        if (note) base += ` \u00b7 ${note}`;
        this.setStatus(base, 'ok');
        this.updateTrigger();
      } else {
        if (this.els.modalPrice) this.els.modalPrice.textContent = '';
        this.setStatus(this.labels.unavailable, 'error');
      }
    } catch (err) {
      console.error('[Borow] availability check failed', err);
      this.state.availability = 0;
      if (this.els.modalPrice) this.els.modalPrice.textContent = '';
      this.setStatus(this.labels.unavailable, 'error');
    }
    this.syncForm();
  }

  /* Rental add-to-cart: intercept the native button so a rental adds via
   * /cart/add.js with its properties and goes straight to the cart page.
   * Buy new is the opposite: the click falls through so the theme's native
   * product-form submits the retail variant with its own cart UX. */
  onAddClick(e) {
    const v = this.activeVariant();
    if (this.state.mode === 'buy') {
      if (!(v && v.available)) { e.preventDefault(); e.stopImmediatePropagation(); }
      return;
    }
    // lockError mirrors syncForm: a basket range this product can't honour must
    // never reach /cart/add.js, however the click got here.
    const ready = !this.state.lockError
      && Boolean(v && v.available && this.state.startDate && this.state.endDate && this.state.availability > 0);
    e.preventDefault();
    e.stopImmediatePropagation();
    if (ready) this.addRentalToCart(v);
  }

  async addRentalToCart(v) {
    if (this.submitBtn) this.submitBtn.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: v.id,
          quantity: 1,
          properties: {
            'Fechas de alquiler': this.rentalDatesLabel(),
            _rental_dates: JSON.stringify({ start: ymd(this.state.startDate), end: ymd(this.state.endDate) }),
            _rental_availability: this.state.availability
          }
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      window.location.href = '/cart';
    } catch (err) {
      console.error('[Borow] add to cart failed', err);
      this.setStatus(this.labels.unavailable, 'error');
      this.syncForm();
    }
  }

  /* Native form */

  activeVariant() {
    const s = this.currentSize();
    if (!s) return null;
    if (this.state.mode === 'buy') return s.retail || null;
    return this.state.snappedDays ? s.rental[this.state.snappedDays] : null;
  }

  syncForm() {
    const v = this.activeVariant();
    // Liquid renders the hidden id input disabled when the product's first
    // variant is unavailable, and with no native picker nothing re-enables
    // it — the native buy-new submit needs it live.
    if (this.idInput && v) { this.idInput.value = v.id; this.idInput.disabled = false; }
    if (this.state.mode === 'buy') {
      // Buy new: the native form submits the retail variant — no rental properties.
      this.setFormProperties(null);
      if (this.submitBtn) this.submitBtn.disabled = !(v && v.available);
      return;
    }
    // A basket lock that can't be honoured on this product is a dead end — keep
    // add-to-cart shut whatever else is in state, so no path re-opens it.
    const ready = !this.state.lockError
      && Boolean(v && v.available && this.state.startDate && this.state.endDate && this.state.availability > 0);
    // "Fechas de alquiler" is deliberately visible (no underscore): Dawn's
    // native property loop renders it in the cart, and Shopify shows it at
    // checkout, on the order status page and in the admin. The underscore
    // properties stay hidden: _rental_dates (JSON) is what the service layer
    // parses into a booking — never change its key or shape.
    this.setFormProperties(ready
      ? {
        'Fechas de alquiler': this.rentalDatesLabel(),
        _rental_dates: JSON.stringify({ start: ymd(this.state.startDate), end: ymd(this.state.endDate) }),
        _rental_availability: this.state.availability
      }
      : null);
    if (this.submitBtn) this.submitBtn.disabled = !ready;
  }

  setFormProperties(props) {
    if (!this.form) return;
    this.form.querySelectorAll('input[data-borow-prop]').forEach((el) => el.remove());
    if (!props) return;
    Object.entries(props).forEach(([k, val]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${k}]`;
      input.value = val;
      input.setAttribute('data-borow-prop', '');
      this.form.appendChild(input);
    });
  }

  /* UI helpers */

  setPrompt(text) { this.setStatus(text, ''); }

  rangeLabel() {
    if (!this.state.startDate) return this.labels.chooseDates;
    const fmt = (d) => this.formatDate(d, { weekday: 'short', day: 'numeric', month: 'short' });
    if (!this.state.endDate) return fill(this.labels.rangeChooseEnd, { start: fmt(this.state.startDate) });
    return fill(this.labels.rangeSummary, {
      days: this.state.snappedDays,
      start: fmt(this.state.startDate),
      end: fmt(this.state.endDate),
    });
  }

  // The storefront language, passed from Liquid as request.locale.iso_code.
  // Every customer-facing date, month name and price formats against it, never
  // against the visitor's device locale.
  localeTag() {
    return this.labels.locale || document.documentElement.lang || undefined;
  }

  // All customer-facing dates go through here, so a Spanish store reads
  // "jue, 15 oct" whatever the visitor's device is set to.
  formatDate(d, options) {
    try { return d.toLocaleDateString(this.localeTag(), options); } catch (_) { return d.toLocaleDateString(undefined, options); }
  }

  // flatpickr bundles English month and weekday names only, so build the
  // storefront-language set from Intl. Names are capitalised for the calendar
  // header: Spanish writes them lower case in prose, but not as a title.
  pickerLocale() {
    const locale = this.localeTag();
    const cap = (s) => s.replace(/\.$/, '').replace(/^./, (c) => c.toUpperCase());
    const names = (count, build, options) => Array.from({ length: count }, (_, i) => {
      try { return cap(new Intl.DateTimeFormat(locale, options).format(build(i))); }
      catch (_) { return ''; }
    });
    const month = (i) => new Date(2021, i, 1);
    // 1 August 2021 was a Sunday: flatpickr indexes weekdays from Sunday
    // whatever firstDayOfWeek is set to.
    const weekday = (i) => new Date(2021, 7, 1 + i);
    const built = {
      firstDayOfWeek: 1,
      months: { longhand: names(12, month, { month: 'long' }), shorthand: names(12, month, { month: 'short' }) },
      weekdays: { longhand: names(7, weekday, { weekday: 'long' }), shorthand: names(7, weekday, { weekday: 'short' }) }
    };
    // Fall back to flatpickr's own English set if Intl gave nothing usable.
    return built.months.longhand[0] ? built : { firstDayOfWeek: 1 };
  }

  updateTrigger() {
    const value = this.els.trigger && this.els.trigger.querySelector('[data-borow-trigger-value]');
    if (!value) return;
    let text = this.rangeLabel();
    // The duration price rides inside the dates field once a range is fixed
    // (client mockup, 24 Aug) - the price row above shows it too.
    if (this.state.snappedDays && this.state.endDate) {
      const price = this.priceText();
      if (price) text += ` · ${price}`;
    }
    value.textContent = text;
  }

  /* Price row above the toggle: label + value follow the mode.
   * Buy -> "Compra nuevo" + retail price of the selected size.
   * Rent, no dates -> "Alquila desde" + cheapest offered duration.
   * Rent, dates fixed -> "Alquiler {days} dias" + that duration's price. */
  updatePrice() {
    if (this.els.modalPrice) this.els.modalPrice.textContent = this.priceText();
    if (!this.els.price) return;
    if (this.state.mode === 'buy') {
      const v = this.activeVariant();
      this.setPriceRow(this.labels.priceBuy, v ? this.variantPrice(v) : '');
    } else if (this.state.snappedDays && this.state.endDate) {
      this.setPriceRow(fill(this.labels.priceRentDays, { days: this.state.snappedDays }), this.priceText());
    } else {
      this.setPriceRow(this.labels.priceRentFrom, this.rentFromPrice());
    }
  }

  setPriceRow(label, value) {
    if (this.els.priceLabel) this.els.priceLabel.textContent = label || '';
    this.els.price.textContent = value || '';
  }

  variantPrice(v) {
    // Liquid's money filter already rendered this in the market's own format,
    // so it matches every other price on the page without guessing here.
    if (v.priceFormatted) return v.priceFormatted;
    return typeof v.price === 'number' ? this.formatMoney(v.price) : '';
  }

  rentFromPrice() {
    const s = this.currentSize();
    if (!s) return '';
    let best = null;
    for (const d of this.availableDurations()) {
      const v = s.rental[d];
      if (v && typeof v.price === 'number' && (best === null || v.price < best.price)) best = v;
    }
    return best ? this.variantPrice(best) : '';
  }

  priceText() {
    const v = this.activeVariant();
    if (!v) return '';
    return this.variantPrice(v);
  }

  // Visible "Fechas de alquiler" line-item property, mirroring the POS
  // formatter (posRentalController.formatRentalDatesLabel) so web and POS
  // orders carry an identical customer-facing line: "jue 15 oct – lun 19 oct
  // 2026 · 4 días". Spanish short forms, lowercase per RAE convention. The
  // end date is the last rental day (returned ON that day), so no "return by"
  // wording. Machine-readable dates stay in _rental_dates; a visible property
  // renders natively in the cart, at checkout, on the order and in the admin.
  rentalDatesLabel() {
    const weekdays = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    const label = (d, withYear) => `${weekdays[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}${withYear ? ` ${d.getFullYear()}` : ''}`;
    const start = this.state.startDate;
    const end = this.state.endDate;
    const sameYear = start.getFullYear() === end.getFullYear();
    const range = `${label(start, !sameYear)} – ${label(end, true)}`;
    const days = this.state.snappedDays;
    if (!(days > 0)) return range;
    return `${range} · ${days} ${days === 1 ? 'día' : 'días'}`;
  }

  lockQuantity() {
    // A rental line is always quantity 1 — set it and hide the theme's PDP quantity selector.
    const qtyInput = document.querySelector('.product-form__quantity input[name="quantity"]');
    if (qtyInput) qtyInput.value = 1;
    const qtyWrap = document.querySelector('.product-form__quantity');
    if (qtyWrap) qtyWrap.hidden = true;
  }

  // Dawn does not ship Shopify.formatMoney, so the fallback is the live path.
  // Format against the storefront language and the market's active currency -
  // variant prices arrive already converted, so labelling them with the shop's
  // base currency would price a GBP market in euros.
  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') return window.Shopify.formatMoney(cents);
    const active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    const currency = active || this.labels.currency || 'EUR';
    try { return (cents / 100).toLocaleString(this.localeTag(), { style: 'currency', currency }); }
    catch (_) { return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'EUR' }); }
  }

  setStatus(text, tone) {
    if (!this.els.status) return;
    this.els.status.textContent = text;
    this.els.status.dataset.tone = tone || '';
  }
}

if (!customElements.get('borow-rental')) customElements.define('borow-rental', BorowRental);
